import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Clock, MapPin, User, Loader2, CheckCircle2, AlertCircle,
  Phone, Video, Building2,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_ICONS = {
  'Phone': Phone,
  'Video': Video,
  'In-Person': Building2,
};

function groupSlotsByDay(slots) {
  const byDay = {};
  for (const s of slots || []) {
    const d = new Date(s.starts_at);
    const key = d.toDateString();
    if (!byDay[key]) byDay[key] = { date: d, slots: [] };
    byDay[key].slots.push(s);
  }
  return Object.values(byDay).sort((a, b) => a.date - b.date);
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

/**
 * PublicSchedule — applicant-facing self-scheduling page.
 *
 * Tokenized URL (no auth). Shows available slots from the eligible interviewer
 * pool for the round configured on the link. Once the applicant picks a slot
 * and confirms, the link is marked used and confirmation emails go out.
 */
export default function PublicSchedule() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/schedule/${token}`);
        if (cancelled) return;
        setData(res.data);
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleBook = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    try {
      const res = await axios.post(`${API}/public/schedule/${token}/book`, {
        interviewer_id: selectedSlot.interviewer_id,
        starts_at: selectedSlot.starts_at,
        ends_at: selectedSlot.ends_at,
      });
      setConfirmed(res.data);
      toast.success('Interview booked!');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center" data-testid="public-schedule-error">
          <AlertCircle size={40} className="text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground">{error}</p>
          <p className="text-xs text-muted-foreground mt-4">If you believe this link should be working, please contact your recruiter.</p>
        </div>
      </div>
    );
  }

  if (confirmed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-xl shadow-lg p-10 max-w-md text-center" data-testid="public-schedule-confirmed">
          <CheckCircle2 size={56} className="text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">You're confirmed!</h2>
          <p className="text-muted-foreground mb-5">A confirmation email is on its way.</p>
          <div className="bg-slate-50 rounded-lg p-4 text-left space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <User size={14} className="mt-0.5 text-muted-foreground" />
              <span>{confirmed.interviewer_name}</span>
            </div>
            <div className="flex items-start gap-2">
              <Calendar size={14} className="mt-0.5 text-muted-foreground" />
              <span>{new Date(confirmed.starts_at).toLocaleString(undefined, {
                weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}</span>
            </div>
            {data?.round?.location && (
              <div className="flex items-start gap-2">
                <MapPin size={14} className="mt-0.5 text-muted-foreground" />
                <span>{data.round.location}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const days = groupSlotsByDay(data?.slots);
  const TypeIcon = TYPE_ICONS[data?.round?.interview_type] || Calendar;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto" data-testid="public-schedule-page">
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold">
            Hi {data?.applicant?.first_name || 'there'}, schedule your {data?.round?.name} interview
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} /> {data?.round?.duration_minutes} minutes
            </span>
            <span className="inline-flex items-center gap-1.5">
              <TypeIcon size={14} /> {data?.round?.interview_type}
            </span>
            {data?.round?.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={14} /> {data.round.location}
              </span>
            )}
          </div>
        </div>

        {days.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-10 text-center text-muted-foreground" data-testid="public-schedule-no-slots">
            <Calendar size={36} className="mx-auto mb-3 text-muted-foreground/50" />
            <p className="font-medium">No openings in the next 14 days</p>
            <p className="text-xs mt-1">Please reach out to your recruiter for help.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {days.map((d, di) => (
              <div key={di} className="bg-white rounded-xl shadow-sm p-5" data-testid={`public-schedule-day-${di}`}>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                  {d.date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {d.slots.map((s, si) => {
                    const isSelected = selectedSlot?.starts_at === s.starts_at && selectedSlot?.interviewer_id === s.interviewer_id;
                    return (
                      <button
                        key={si}
                        onClick={() => setSelectedSlot(s)}
                        data-testid={`public-schedule-slot-${di}-${si}`}
                        className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition ${
                          isSelected
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-slate-200 hover:border-primary hover:bg-primary/5'
                        }`}
                      >
                        <div>{formatTime(s.starts_at)}</div>
                        <div className={`text-xs mt-0.5 ${isSelected ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                          {s.interviewer_name}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {selectedSlot && (
          <div className="sticky bottom-4 mt-6">
            <div className="bg-white rounded-xl shadow-lg border-2 border-primary p-4 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  {new Date(selectedSlot.starts_at).toLocaleString(undefined, {
                    weekday: 'short', month: 'short', day: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  })}
                </p>
                <p className="text-xs text-muted-foreground">with {selectedSlot.interviewer_name}</p>
              </div>
              <Button
                onClick={handleBook}
                disabled={booking}
                size="lg"
                data-testid="public-schedule-confirm-btn"
                className="gap-2"
              >
                {booking ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
                {booking ? 'Booking…' : 'Confirm this time'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
